/**
 * Search Overlay Web Component
 * Full-screen search dialog with Shopify Predictive Search API integration.
 *
 * Uses Shopify's native Predictive Search API for instant product results
 * as the user types. This overlay handles autocomplete/instant search.
 */
import { ABBaseComponent, defineComponent } from './ab-base-component.js';

class ABSearchOverlay extends ABBaseComponent {
  connectedCallback() {
    this.dialog = this.$('dialog');
    this.input = this.$('input[name="q"]');
    this.clearBtn = this.$('[data-clear-search]');
    this.viewAllLink = this.$('[data-search-view-all]');
    this.resultsContainer = this.$('[data-search-results]');
    this.resultsHeading = this.$('[data-search-results-heading]');
    this.staticContent = this.$('[data-search-fallback]');
    this.searchTriggers = document.querySelectorAll('[data-search-trigger]');

    this._debounceTimer = null;
    this._abortController = null;
    this._lastQuery = '';

    this.onTriggerClick = () => this.open();
    this.onCloseClick = () => this.close();
    this.onClearClick = () => this.clearInput();
    this.onBackdropClick = (e) => {
      if (e.target === this.dialog) this.close();
    };
    this.onDialogCancel = (e) => {
      e.preventDefault();
      this.close();
    };
    this.onDocumentKeydown = (e) => {
      if (e.key === 'Escape' && this.dialog?.open) {
        e.preventDefault();
        this.close();
      }
    };
    this.onInputChange = () => {
      this.updateViewAllHref();
      this.toggleClearButton();
      this._debouncedSearch();
    };

    this.searchTriggers.forEach(trigger => {
      trigger.addEventListener('click', this.onTriggerClick);
    });

    this.$('[data-close-search]')?.addEventListener('click', this.onCloseClick);
    this.clearBtn?.addEventListener('click', this.onClearClick);
    this.input?.addEventListener('input', this.onInputChange);

    this.dialog?.addEventListener('click', this.onBackdropClick);
    this.dialog?.addEventListener('cancel', this.onDialogCancel);
    document.addEventListener('keydown', this.onDocumentKeydown);
  }

  disconnectedCallback() {
    this.searchTriggers?.forEach(trigger => {
      trigger.removeEventListener('click', this.onTriggerClick);
    });
    this.$('[data-close-search]')?.removeEventListener('click', this.onCloseClick);
    this.clearBtn?.removeEventListener('click', this.onClearClick);
    this.input?.removeEventListener('input', this.onInputChange);
    this.dialog?.removeEventListener('click', this.onBackdropClick);
    this.dialog?.removeEventListener('cancel', this.onDialogCancel);
    document.removeEventListener('keydown', this.onDocumentKeydown);
  }

  open() {
    this.lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.dialog?.showModal();
    this.setTriggerState(true);
    this.updateViewAllHref();

    setTimeout(() => {
      if (!this.input) return;
      this.input.focus();

      // If there's a cached query, re-run search
      if (this.input.value.trim().length >= 2) {
        this._debouncedSearch();
      }
    }, 50);
  }

  close() {
    this._cancelSearch();

    if (this.dialog?.open) {
      this.dialog.close();
    }

    if (this.input) {
      this.input.value = '';
    }

    this._clearResults();
    this.setTriggerState(false);
    this.updateViewAllHref();

    this.lastFocusedElement?.focus?.();
    this.lastFocusedElement = null;
  }

  setTriggerState(isOpen) {
    this.searchTriggers?.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      trigger.classList.toggle('is-active', isOpen);
    });
  }

  clearInput() {
    if (this.input) {
      this.input.value = '';
      this.input.focus();
    }
    this._cancelSearch();
    this._clearResults();
    this.toggleClearButton();
    this.updateViewAllHref();
  }

  toggleClearButton() {
    if (!this.clearBtn) return;
    const hasValue = this.input?.value.trim().length > 0;
    this.clearBtn.hidden = !hasValue;
  }

  updateViewAllHref() {
    if (!this.viewAllLink) return;
    const query = (this.input?.value || '').trim();
    this.viewAllLink.href = query ? `/search?q=${encodeURIComponent(query)}` : '/search';
    this.viewAllLink.style.display = query ? 'block' : 'none';
  }

  /* ── Predictive Search API ── */

  _debouncedSearch() {
    clearTimeout(this._debounceTimer);
    const query = this.input?.value?.trim();

    if (!query || query.length < 2) {
      this._clearResults();
      return;
    }

    this._debounceTimer = setTimeout(() => this._fetchResults(query), 250);
  }

  _cancelSearch() {
    clearTimeout(this._debounceTimer);
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }
  }

  async _fetchResults(query) {
    if (query === this._lastQuery) return;
    this._lastQuery = query;

    // Abort any in-flight request
    if (this._abortController) this._abortController.abort();
    this._abortController = new AbortController();

    try {
      const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection&resources[limit]=4&resources[options][fields]=title,product_type,vendor,variants.title,tag`;
      const res = await fetch(url, { signal: this._abortController.signal });
      if (!res.ok) return;
      const data = await res.json();
      this._renderResults(data.resources?.results, query);
    } catch (err) {
      if (err.name !== 'AbortError') {
        // Network error — silently degrade
      }
    }
  }

  _renderResults(results, query) {
    if (!this.resultsContainer) return;

    const products = results?.products || [];
    const collections = results?.collections || [];

    // Show/hide static content based on results
    if (this.staticContent) {
      this.staticContent.style.display = products.length || collections.length ? 'none' : '';
    }

    // Update heading
    if (this.resultsHeading) {
      this.resultsHeading.style.display = products.length || collections.length ? '' : 'none';
    }

    if (!products.length && !collections.length) {
      if (query.length >= 2) {
        this.resultsContainer.innerHTML = `
          <div class="ab-search-results__empty">
            No results found for "<strong>${this._esc(query)}</strong>"
          </div>`;
      } else {
        this.resultsContainer.innerHTML = '';
      }
      return;
    }

    let html = '';

    // Product results — grid of cards
    if (products.length) {
      html += '<div class="ab-search-results__products">';
      for (const p of products) {
        const img = p.image
          ? `<img src="${this._esc(this._resizeImage(p.image, '200x200'))}" alt="${this._esc(p.title)}" width="100" height="100" loading="lazy" class="ab-search-results__product-img">`
          : '<div class="ab-search-results__product-img ab-search-results__product-img--placeholder"></div>';

        const price = this._formatPrice(p.price);
        const comparePrice = p.compare_at_price_max && parseFloat(p.compare_at_price_max) > parseFloat(p.price)
          ? `<span class="ab-search-results__compare-price">${this._formatPrice(p.compare_at_price_max)}</span>`
          : '';

        html += `
          <a href="${this._esc(p.url)}" class="ab-search-results__product">
            ${img}
            <div class="ab-search-results__product-info">
              <span class="ab-search-results__product-title">${this._esc(p.title)}</span>
              <span class="ab-search-results__product-price">${comparePrice}${price}</span>
            </div>
          </a>`;
      }
      html += '</div>';
    }

    // Collection results — pill links
    if (collections.length) {
      html += '<div class="ab-search-results__collections">';
      html += '<p class="ab-search-results__collections-label">Collections</p>';
      html += '<div class="ab-search-results__collections-list">';
      for (const c of collections) {
        html += `<a href="${this._esc(c.url)}" class="ab-search-results__collection-pill">${this._esc(c.title)}</a>`;
      }
      html += '</div></div>';
    }

    this.resultsContainer.innerHTML = html;
  }

  _clearResults() {
    this._lastQuery = '';
    if (this.resultsContainer) this.resultsContainer.innerHTML = '';
    if (this.staticContent) this.staticContent.style.display = '';
    if (this.resultsHeading) this.resultsHeading.style.display = 'none';
  }

  _formatPrice(price) {
    const num = parseFloat(price);
    if (isNaN(num)) return '';
    return `$${num.toFixed(2)}`;
  }

  _resizeImage(url, size) {
    // Shopify image URL resizing: insert _SIZExSIZE before file extension
    if (!url) return url;
    return url.replace(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i, `_${size}.$1$2`);
  }

  _esc(str) {
    const div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }
}

defineComponent('ab-search-overlay', ABSearchOverlay);
